import { create } from 'zustand';
import {
	OfferListItem,
	OffersListResponse,
	OfferStatus,
	PaginationData,
	SeasonalOneTimeOffer,
} from '../types';

interface SpecialistOffersState {
	// Listing state
	offers: OfferListItem[];
	isLoading: boolean;
	error: string | null;
	pagination: PaginationData;

	// Filters
	status: OfferStatus;

	// Selected offer details
	selectedOffer: SeasonalOneTimeOffer | null;
	isOfferDetailLoading: boolean;
	offerDetailError: string | null;

	// Actions
	setOffers: (offers: OffersListResponse) => void;
	setStatus: (status: OfferStatus) => void;
	setIsLoading: (isLoading: boolean) => void;
	setError: (error: string | null) => void;
	setSelectedOffer: (offer: SeasonalOneTimeOffer | null) => void;
	setIsOfferDetailLoading: (isLoading: boolean) => void;
	setOfferDetailError: (error: string | null) => void;

	// Reset state
	resetState: () => void;
}

// Initial state to avoid duplication
const initialState = {
	// Listing state
	offers: [] as OfferListItem[],
	isLoading: false,
	error: null as string | null,
	pagination: {
		total_pages: 0,
		current_page: 1,
		items_per_page: 10,
		total_items: 0,
	},

	// Filters
	status: 'active' as OfferStatus,

	// Selected offer details
	selectedOffer: null as SeasonalOneTimeOffer | null,
	isOfferDetailLoading: false,
	offerDetailError: null as string | null,
};

export const useSpecialistOffersStore = create<SpecialistOffersState>((set) => ({
	...initialState,

	// Actions
	setOffers: (offersResponse: OffersListResponse) => {
		console.log(`[Store] Setting ${offersResponse.data.length} offers`);
		set((state) => ({
			offers: offersResponse.data,
			pagination: offersResponse.pagination,
			isLoading: false,
			error: null,
		}));
	},

	setStatus: (status: OfferStatus) => {
		console.log(`[Store] Setting status to: ${status}`);
		set((state) => {
			// Only update if the status is actually changing
			if (state.status === status) {
				return state; // No change needed
			}
			
			return {
				status,
				// Reset offers and set loading to true when status changes
				offers: [],
				error: null,
				isLoading: true,
			};
		});
	},

	setIsLoading: (isLoading: boolean) => {
		console.log(`[Store] Setting isLoading to: ${isLoading}`);
		set((state) => ({ isLoading }));
	},

	setError: (error: string | null) => {
		console.log(`[Store] Setting error: ${error}`);
		set((state) => ({ error, isLoading: false }));
	},

	setSelectedOffer: (offer: SeasonalOneTimeOffer | null) => {
		console.log(`[Store] Setting selected offer: ${offer?.id || 'null'}`);
		set((state) => ({
			selectedOffer: offer,
			isOfferDetailLoading: false,
			offerDetailError: null,
		}));
	},

	setIsOfferDetailLoading: (isLoading: boolean) => {
		console.log(`[Store] Setting isOfferDetailLoading to: ${isLoading}`);
		set((state) => ({ isOfferDetailLoading: isLoading }));
	},

	setOfferDetailError: (error: string | null) => {
		console.log(`[Store] Setting offerDetailError: ${error}`);
		set((state) => ({ offerDetailError: error, isOfferDetailLoading: false }));
	},

	// Reset state
	resetState: () => {
		console.log('[Store] Resetting state');
		set((state) => ({ ...initialState }));
	},
}));
